import _ from 'lodash';
import React from 'react';
import PropTypes from 'prop-types';
import DirectionsBikeIcon from '@material-ui/icons/DirectionsBike';
import TerrainIcon from '@material-ui/icons/Terrain';
import MapIcon from '@material-ui/icons/Map';
import {
  BooleanField,
  BooleanInput,
  Button,
  Create,
  Edit,
  Filter,
  FunctionField,
  ImageField,
  List,
  LongTextInput,
  NumberField,
  NumberInput,
  ReferenceInput,
  Show,
  SimpleForm,
  SimpleShowLayout,
  TextField,
  TextInput,
  UrlField,
  required,
} from 'react-admin';
import { remakeRouteMap } from '../api';
import { withLoading } from '../sagas';
import Datagrid from './detail/Datagrid';
import { FormHelperText, withStyles } from '@material-ui/core';
import Query from './data/Query';
import ShowButton from './button/ShowButton';
import ShowActions from './detail/ShowActions';
import EditActions from './detail/EditActions';
import LongTextField from './fields/LongTextField';
import LLRUploadInput from './inputs/LLRUploadInput';
import SelectInput from './inputs/SelectInput';
import SelectArrayInput from './inputs/SelectArrayInput';
import { getChoices, ellipsize } from '../utils';
import ManyToManyInputHelper from './data/ManyToManyInputHelper';
import ReferenceManyToManyField from './data/ReferenceManyToManyField';
import { FormDataConsumer } from 'ra-core';
import MultiLangBlock from './other/MultiLangBlock';
import { QUERY_PER_PAGE } from '../constants';

const styles = {
  multiSelect: {
    minWidth: '256px',
    maxWidth: '512px',
  },
};

const TITLE_SINGULAR = 'Route';
const TITLE_PLURAL = 'Routes';

const ACTIVITY_TYPE_CHOICES = [
  { id: 'bike', name: 'Bike', icon: DirectionsBikeIcon },
  { id: 'hike', name: 'Hike', icon: TerrainIcon },
];

const ActivityTypeOptionRenderer = choice => (
  <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
    {choice.icon && React.createElement(choice.icon, { style: { fontSize: '1.2rem' } })}
    {choice.name}
  </span>
);

const ActivityTypeField = ({ record }) => {
  const isBike = record.activityType === 'bike';
  return (
    <span style={{ display: 'flex', alignItems: 'center' }} title={isBike ? 'Bike' : 'Hike'}>
      {isBike ? <DirectionsBikeIcon /> : <TerrainIcon />}
    </span>
  );
};

const RouteTitle = ({ record }) => (
  <span>
    {TITLE_SINGULAR} "{record.name}"
  </span>
);
RouteTitle.propTypes = { record: PropTypes.shape({ name: PropTypes.string }) };

const RouteFilters = props => (
  <Filter {...props}>
    <TextInput label="Search" source="q__internalName_name" alwaysOn />
    <SelectInput
      label="Activity"
      source="activityType"
      choices={ACTIVITY_TYPE_CHOICES}
      optionText={ActivityTypeOptionRenderer}
      alwaysOn
    />
    <ReferenceInput
      perPage={QUERY_PER_PAGE}
      source="regionId"
      reference="region"
      label="Region"
      alwaysOn
    >
      <SelectInput />
    </ReferenceInput>
    <BooleanInput label="Show archived" source="includeArchived" alwaysOn />
  </Filter>
);

// Rendered as the List's only child, so React Admin injects filterValues here,
// letting us show the Archived column only while "Show archived" is enabled.
const RouteDatagrid = ({ filterValues, ...props }) => (
  <Datagrid rowClick="edit" filterValues={filterValues} {...props}>
    <FunctionField label="Activity" render={record => <ActivityTypeField record={record} />} />
    <TextField source="internalName" />
    <ReferenceManyToManyField
      linkResName="routeToRegionLink"
      fromResIdName="routeId"
      toResIdName="regionId"
      toResName="region"
      label="Regions"
    />
    <ImageField source="imageUrl" label="Photo" />
    <ImageField source="mapUrl" label="Map" />
    {filterValues && filterValues.includeArchived && (
      <BooleanField source="isArchived" label="Archived" />
    )}
    <ShowButton />
  </Datagrid>
);

export const RouteList = props => (
  <List
    title={TITLE_PLURAL}
    {...props}
    filters={<RouteFilters />}
    filterDefaultValues={{ includeArchived: false }}
    sort={{ field: 'internalName', order: 'ASC' }}
  >
    <RouteDatagrid />
  </List>
);

export const RouteShow = props => (
  <Show title={<RouteTitle />} actions={<ShowActions />} {...props}>
    <SimpleShowLayout>
      <FunctionField label="Activity Type" render={record => <ActivityTypeField record={record} />} />
      <UrlField source="gpxUrl" />
      <TextField source="internalName" />
      <TextField source="name" />

      <ImageField source="mapUrl" label="Map" />
      <ImageField source="imageUrl" label="Photo" />
      <ImageField source="altimetryUrl" label="Altimetry image" />
      <NumberField source="distanceKms" />
      <NumberField source="altimetry" label="Altimetry meters" />
      <MultiLangBlock isShow>
        <LongTextField source="description" label="Description" />
      </MultiLangBlock>
      <ReferenceManyToManyField
        linkResName="routeToRegionLink"
        fromResIdName="routeId"
        toResIdName="regionId"
        toResName="region"
        label="Regions"
      />
      <ReferenceManyToManyField
        linkResName="poiToRouteLink"
        fromResIdName="routeId"
        toResIdName="pointOfInterestId"
        toResName="pointOfInterest"
        label="Points of interest"
      />
      <FormDataConsumer>
        {({ formData, ...rest }) =>
          formData.activityType === 'bike' && (
            <ReferenceManyToManyField
              linkResName="bikeShopToRouteLink"
              fromResIdName="routeId"
              toResIdName="bikeShopId"
              toResName="bikeShop"
              label="Bike shops"
              {...rest}
            />
          )
        }
      </FormDataConsumer>
    </SimpleShowLayout>
  </Show>
);
RouteShow.propTypes = {
  location: PropTypes.object.isRequired,
};

const PointsOfInterestSelectField = ({ record, isChip }) => (
  <div style={{ display: 'flex', flexDirection: 'column', lineHeight: '0.95rem' }}>
    <span>{record.name}</span>
    {!isChip && record.address && (
      <span style={{ color: '#aaa', fontSize: 10, lineHeight: '0.95rem' }}>
        {ellipsize(record.address, 50)}
      </span>
    )}
  </div>
);

const PointsOfInterestInput = withStyles(styles)(({ classes }) => (
  <FormDataConsumer>
    {({ formData }) =>
      formData && (
        <Query
          onlyData
          type="GET_ALL"
          resource="pointOfInterest"
          errorMsg="Failed to fetch points of interest."
        >
          {pois => (
            <Query
              onlyData
              type="GET_ALL"
              resource="poiToRegionLink"
              errorMsg="Failed to fetch points of interests."
            >
              {poiToRegionLinks => (
                <Query
                  onlyData
                  type="GET_ALL"
                  resource="poiCategory"
                  errorMsg="Failed to fetch points of interests."
                >
                  {categories => {
                    const regionIdsByPoiId = _.mapValues(
                      _.groupBy(poiToRegionLinks, 'pointOfInterestId'),
                      links => links.map(link => link.regionId)
                    );
                    const filteredPois = pois.filter(
                      poi =>
                        formData.pois.indexOf(poi.id) !== -1 ||
                        (regionIdsByPoiId[poi.id] &&
                          formData.regions &&
                          regionIdsByPoiId[poi.id].filter(regionId =>
                            formData.regions.includes(regionId)
                          ).length >= 1)
                    );
                    const choicesWithCategories = getChoices(
                      filteredPois,
                      categories,
                      'categoryId',
                      {
                        type: 'grouped',
                        sortBy: ['categoryId', 'name'],
                        extraFields: ['address'],
                      }
                    );

                    return (
                      <SelectArrayInput
                        className={classes.multiSelect}
                        label="Points of interest"
                        source="pois"
                        choices={choicesWithCategories}
                        optionText={<PointsOfInterestSelectField />}
                      />
                    );
                  }}
                </Query>
              )}
            </Query>
          )}
        </Query>
      )
    }
  </FormDataConsumer>
));

const BikeShopsInput = withStyles(styles)(({ classes }) => (
  <FormDataConsumer>
    {({ formData }) =>
      formData && (
        <Query onlyData type="GET_ALL" resource="bikeShop" errorMsg="Failed to fetch bike shop.">
          {bikeShops => (
            <Query
              onlyData
              type="GET_ALL"
              resource="bikeShopToRegionLink"
              errorMsg="Failed to fetch bike shops."
            >
              {bikeShopToRegionLinks => {
                const regionIdsByBikeShopId = _.mapValues(
                  _.groupBy(bikeShopToRegionLinks, 'bikeShopId'),
                  links => links.map(link => link.regionId)
                );
                const filteredBikeShops = bikeShops.filter(
                  bikeShop =>
                    formData.bikeShops.indexOf(bikeShop.id) !== -1 ||
                    (regionIdsByBikeShopId[bikeShop.id] &&
                      formData.regions &&
                      regionIdsByBikeShopId[bikeShop.id].filter(regionId =>
                        formData.regions.includes(regionId)
                      ).length >= 1)
                );

                return (
                  <SelectArrayInput
                    className={classes.multiSelect}
                    label="Bike Shops"
                    source="bikeShops"
                    choices={filteredBikeShops}
                  />
                );
              }}
            </Query>
          )}
        </Query>
      )
    }
  </FormDataConsumer>
));

const RegenerateMapButton = ({ record }) => {
  const routeId = record && record.id ? parseInt(record.id, 10) : null;
  if (!routeId) return null;
  const handleRemakeMap = withLoading({ refresh: true })(async () => {
    await remakeRouteMap(routeId);
  });
  return (
    <div style={{ marginTop: 16, marginBottom: 16 }}>
      <Button label="Regenerate Map from GPX" variant="contained" onClick={handleRemakeMap}>
        <MapIcon />
      </Button>
    </div>
  );
};

export const RouteEdit = props => (
  <Edit title={<RouteTitle />} actions={<EditActions />} {...props}>
    <ManyToManyInputHelper
      configs={[
        {
          source: 'regions',
          linkResName: 'routeToRegionLink',
          fromResIdName: 'routeId',
          toResIdName: 'regionId',
          toResName: 'region',
          label: 'Regions',
        },
        {
          source: 'pois',
          linkResName: 'poiToRouteLink',
          fromResIdName: 'routeId',
          toResIdName: 'pointOfInterestId',
          toResName: 'pointOfInterest',
          label: 'Points of interest',
        },
        {
          source: 'bikeShops',
          linkResName: 'bikeShopToRouteLink',
          fromResIdName: 'routeId',
          toResIdName: 'bikeShopId',
          toResName: 'bikeShop',
          label: 'Bike Shops',
        },
      ]}
    >
      {(editProps, defaultValue, [RegionsInput]) => (
        <SimpleForm defaultValue={defaultValue} {...editProps}>
          <LLRUploadInput
            source="gpxUrl"
            label="GPX file"
            type="file"
            extension="gpx"
            validate={[required()]}
          />
          <>
            <FormHelperText disabled>
              NOTE: Uploading a GPX file will override Name, Map, Distance Kms and Altimetry fields.
            </FormHelperText>
          </>
          <LLRUploadInput
            source="fitUrl"
            label="FIT file (for turn navigation)"
            type="file"
            extension="fit"
          />
          <>
            <FormHelperText disabled>
              NOTE: FIT file is optional. It provides turn-by-turn navigation cues.
            </FormHelperText>
          </>
          <SelectInput
            source="activityType"
            label="Activity Type"
            choices={ACTIVITY_TYPE_CHOICES}
            optionText={ActivityTypeOptionRenderer}
            validate={[required()]}
          />
          <TextInput source="internalName" validate={[required()]} />
          <TextInput source="name" validate={[required()]} />
          <LLRUploadInput source="mapUrl" label="Map" type="image" validate={[required()]} />
          <RegenerateMapButton />
          <LLRUploadInput
            source="altimetryUrl"
            label="Altimetry image"
            type="image"
            validate={[required()]}
          />
          <NumberInput source="distanceKms" validate={[required()]} />
          <NumberInput source="altimetry" label="Altimetry meters" validate={[required()]} />
          <LLRUploadInput
            source="imageUrl"
            label="Image (720x560)"
            type="image"
            validate={[required()]}
          />
          <MultiLangBlock>
            <LongTextInput source="description" label="Description" validate={[required()]} />
          </MultiLangBlock>
          <RegionsInput />
          <PointsOfInterestInput />
          <FormDataConsumer>
            {({ formData, ...rest }) =>
              formData.activityType === 'bike' && <BikeShopsInput {...rest} />
            }
          </FormDataConsumer>
          <BooleanInput source="isArchived" label="Archived" />
        </SimpleForm>
      )}
    </ManyToManyInputHelper>
  </Edit>
);
RouteEdit.propTypes = {
  id: PropTypes.string.isRequired,
};

export const RouteCreate = props => (
  <Create {...props}>
    <SimpleForm>
      <SelectInput
        source="activityType"
        label="Activity Type"
        choices={ACTIVITY_TYPE_CHOICES}
        optionText={ActivityTypeOptionRenderer}
        defaultValue="bike"
        validate={[required()]}
      />
      <LLRUploadInput
        source="gpxUrl"
        label="GPX file"
        type="file"
        extension="gpx"
        validate={[required()]}
      />
      <LLRUploadInput
        source="fitUrl"
        label="FIT file (for turn navigation)"
        type="file"
        extension="fit"
      />
      <>
        <FormHelperText disabled>
          NOTE: FIT file is optional. It provides turn-by-turn navigation cues.
        </FormHelperText>
      </>
      <TextInput source="internalName" validate={[required()]} />
      <TextInput source="name" validate={[required()]} />
      <LLRUploadInput source="mapUrl" label="Map" type="image" validate={[required()]} />
      <LLRUploadInput
        source="altimetryUrl"
        label="Altimetry image"
        type="image"
        validate={[required()]}
      />
      <NumberInput source="distanceKms" validate={[required()]} />
      <NumberInput source="altimetry" label="Altimetry meters" validate={[required()]} />
      <LLRUploadInput
        source="imageUrl"
        type="image"
        label="Image (720x560)"
        validate={[required()]}
      />
      <MultiLangBlock>
        <LongTextInput source="description" label="Description" validate={[required()]} />
      </MultiLangBlock>
    </SimpleForm>
  </Create>
);
